import classNames from "classnames";
import { BuilderSimpleCartProps } from "../BuilderInterface";
import "../BuilderSimpleCart/style.scss";
import { NodeStates, SimpleCartNode } from "../../build/types";
import {
    getColorsFromInteractiveStates,
    getCompoundStyle,
    getStateStringForBuilder,
    getStateStyleForBuilder,
    getStylesFromInteractiveStates,
    getStyleStringFromInteractiveState
} from "../helpers";
import SimpleCartProduct from "../BuilderSimpleCart/SimpleCartProduct";
import SimpleCartFooter from "../BuilderSimpleCart/SimpleCartFooter";
import "../BuilderSimpleCart/style.scss";
import { useInteractionStates } from "../../hooks/useInteractionStates";
import SimpleCartCustomer from "../BuilderSimpleCart/SimpleCartCustomer";
import DynamicIcon from "../DynamicIcon";
import SimpleCartCustomSales from "../BuilderSimpleCart/SimpleCartCustomSales";

const BuilderSimpleCartUnfixed = ({
    node,
    taxIncl,
    calculatedTaxDetails,
    activeCartData,
    builderProps,
    dynamicData,
    formatCurrency,
    payments,
    disabled,
    emptyState,
    children,
    isCFD,
    onProductRemove,
    onCartFeeRemove,
    onRemoveDiscount,
    onProductSelection,
    onCustomSaleSelection,
    onCustomSaleRemove,
    onEditCustomer,
    onRemoveCustomer
}: BuilderSimpleCartProps) => {
    const cNode = node as SimpleCartNode;
    const { id, attr, expandedNode, stateManager, prepend, append, fallback } = cNode;
    const { products, subtotal, total, tax, discount, customFee, customer, orderNotes, cartTotal, customSales } = activeCartData;

    const formatC = formatCurrency || ((amount: number) => amount.toFixed(2));

    const { setElement, states } = useInteractionStates<HTMLElement>();

    const builderSimpleCartClass = classNames("builder-simple-cart unfixed-mode", {
        [`${attr?.className}`]: !!attr?.className
    });

    if (builderProps) {
        const { nodeState, selectCallback, outlineCallback, onDragStart, onDragEnd } = builderProps;

        const stateCartWidth =
            getStateStringForBuilder(stateManager, nodeState, "expandedNode_cartWidth") || expandedNode.cartWidth;
        const stateCartHeight =
            getStateStringForBuilder(stateManager, nodeState, "expandedNode_cartHeight") || expandedNode.cartHeight;
        const stateCartBG = getStateStringForBuilder(stateManager, nodeState, "expandedNode_mainBg") || expandedNode.mainBg;
        const stateDividerColor =
            getStateStringForBuilder(stateManager, nodeState, "expandedNode_dividerColor") || expandedNode.dividerColor;
        const stateTotalBG = getStateStringForBuilder(stateManager, nodeState, "expandedNode_totalBg") || expandedNode.totalBg;

        const customerIcon = (
            <DynamicIcon
                name={expandedNode?.customerIcon?.src || ""}
                style={getCompoundStyle({
                    otherStyle: expandedNode?.customerIcon?.style,
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "expandedNode_customerIcon_style")
                })}
                colors={expandedNode?.customerIcon?.colors}
            />
        );

        const customerXIcon = (
            <DynamicIcon
                name={expandedNode?.customerXIcon?.src || ""}
                style={getCompoundStyle({
                    otherStyle: expandedNode?.customerXIcon?.style,
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "expandedNode_customerXIcon_style")
                })}
                colors={expandedNode?.customerXIcon?.colors}
            />
        );

        const fallbackStyle = getCompoundStyle({
            node,
            otherStyle: fallback?.style || {},
            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "fallback_style")
        });
        const prependStyle = getCompoundStyle({
            node,
            otherStyle: prepend?.style || {},
            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "prepend_style")
        });

        const appendStyle = getCompoundStyle({
            node,
            otherStyle: append?.style || {},
            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "append_style")
        });

        return (
            <div
                id={id}
                {...attr}
                className={builderSimpleCartClass}
                style={getCompoundStyle({
                    node,
                    otherStyle: {
                        width: stateCartWidth,
                        height: stateCartHeight
                    },
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "node_attr")
                })}
                onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                onDragStart={e => onDragStart?.(e, id)}
                draggable={"true"}
                onDragEnd={onDragEnd}
            >
                <aside
                    className="expanded-cart unfixed"
                    style={getCompoundStyle({
                        otherStyle: {
                            // ...(stateCartWidth ? { width: stateCartWidth } : {}),
                            // ...(stateCartHeight ? { height: stateCartHeight } : {}),
                            ...(stateCartBG ? { backgroundColor: stateCartBG } : {})
                        }
                    })}
                >
                    <section>
                        {emptyState ? (
                            children
                        ) : (
                            <>
                                {cNode.expandedNode.showActiveCustomer !== false && customer && (
                                    <SimpleCartCustomer
                                        dynamicData={dynamicData || ""}
                                        customerIcon={customerIcon}
                                        closeCustomerIcon={customerXIcon}
                                        customerTypography={getCompoundStyle({
                                            otherStyle: expandedNode.typography?.customer,
                                            stateStyle: getStateStyleForBuilder(
                                                stateManager,
                                                nodeState,
                                                "expandedNode_typography_customer"
                                            )
                                        })}
                                        customerChipStyle={getCompoundStyle({
                                            otherStyle: expandedNode.customerChip,
                                            stateStyle: getStateStyleForBuilder(
                                                stateManager,
                                                nodeState,
                                                "expandedNode_customerChip"
                                            )
                                        })}
                                        onClose={() => console.log("Close")}
                                        onEdit={() => console.log("Edit")}
                                        prepend={{ style: prependStyle, value: prepend?.value || "" }}
                                        append={{ style: appendStyle, value: append?.value || "" }}
                                        fallback={{ style: fallbackStyle, value: fallback?.value || "" }}
                                    />
                                )}

                                {[...products].reverse().map((p, index) => (
                                    <SimpleCartProduct
                                        key={index}
                                        product={p}
                                        dividerBg={stateDividerColor}
                                        productStyle={getCompoundStyle({
                                            otherStyle: expandedNode.typography?.product,
                                            stateStyle: getStateStyleForBuilder(
                                                stateManager,
                                                nodeState,
                                                "expandedNode_typography_product"
                                            )
                                        })}
                                        productSubtextStyle={getCompoundStyle({
                                            otherStyle: expandedNode.typography?.productSubtext,
                                            stateStyle: getStateStyleForBuilder(
                                                stateManager,
                                                nodeState,
                                                "expandedNode_typography_productSubtext"
                                            )
                                        })}
                                        expandedNode={expandedNode}
                                        formatCurrency={formatC}
                                        stateManager={stateManager}
                                        builder
                                        nodeState={nodeState}
                                    />
                                ))}

                                {expandedNode.showOrderNotes && (
                                    <div style={{ display: "flex", flexDirection: "column" }}>
                                        <span
                                            className="cart-order-notes-label"
                                            style={getCompoundStyle({
                                                otherStyle: expandedNode.typography?.orderNoteLabel,
                                                stateStyle: getStateStyleForBuilder(
                                                    stateManager,
                                                    nodeState,
                                                    "expandedNode_typography_orderNoteLabel"
                                                )
                                            })}
                                        >
                                            {expandedNode.showOrderNotesLabel && expandedNode.orderNotesLabel}
                                        </span>
                                        <span
                                            className="cart-order-notes"
                                            style={getCompoundStyle({
                                                otherStyle: expandedNode.typography?.orderNote,
                                                stateStyle: getStateStyleForBuilder(
                                                    stateManager,
                                                    nodeState,
                                                    "expandedNode_typography_orderNote"
                                                )
                                            })}
                                        >
                                            {orderNotes}
                                        </span>
                                    </div>
                                )}
                            </>
                        )}
                    </section>
                    <SimpleCartFooter
                        tax={tax}
                        total={total}
                        taxIncl={taxIncl}
                        bg={stateTotalBG}
                        discount={discount}
                        subtotal={subtotal}
                        showTaxes={expandedNode.showTaxes}
                        taxesLabel={expandedNode.customizeTaxes}
                        dividerBg={stateDividerColor || expandedNode.dividerColor}
                        totalStyle={getCompoundStyle({
                            otherStyle: expandedNode.typography?.total,
                            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "expandedNode_typography_total")
                        })}
                        subtotalStyle={getCompoundStyle({
                            otherStyle: expandedNode.typography?.subtotals,
                            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "expandedNode_typography_subtotals")
                        })}
                        onRemoveDiscount={onRemoveDiscount}
                        showDiscount={expandedNode.showCartDiscount}
                        showSubtotal={expandedNode.showCartSubtotal}
                        subtotalLabel={expandedNode.customizeCartSubtotal}
                        showTotal={expandedNode.showCartTotal}
                        totalLabel={expandedNode.customizeCartTotal}
                        formatCurrency={formatC}
                        balanceLabel={expandedNode.balanceDueLabel}
                        showBalance={expandedNode.showBalance}
                        payments={payments}
                        cartFees={customFee}
                        calculatedCartFees={calculatedTaxDetails?.cartFees}
                        cartTotal={cartTotal}
                        orderTotalLabel={expandedNode.orderTotalLabel}
                        showOrderTotal={expandedNode.showOrderTotal}
                        summarySubtextStyle={getCompoundStyle({
                            otherStyle: expandedNode?.typography?.summarySubtext,
                            stateStyle: getStateStyleForBuilder(stateManager, nodeState, "expandedNode_typography_summarySubtext")
                        })}
                        onCartFeeRemove={onCartFeeRemove}
                        // cartTotalLabel={expandedNode.cart}
                    />
                </aside>
            </div>
        );
    } else {
        //Aside
        const cartWidth =
            getStyleStringFromInteractiveState({
                states,
                stateManager,
                path: "expandedNode_cartWidth",
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            }) || expandedNode.cartWidth;

        const mainBg =
            getStyleStringFromInteractiveState({
                states,
                stateManager,
                path: "expandedNode_mainBg",
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            }) || expandedNode.mainBg;

        const cartHeight =
            getStyleStringFromInteractiveState({
                states,
                stateManager,
                path: "expandedNode_cartHeight",
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            }) || expandedNode.cartHeight;

        const dividerColor =
            getStyleStringFromInteractiveState({
                states,
                stateManager,
                path: "expandedNode_dividerColor",
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            }) || expandedNode.dividerColor;

        const totalBG =
            getStyleStringFromInteractiveState({
                states,
                stateManager,
                path: "expandedNode_totalBg",
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            }) || expandedNode.totalBg;

        const cartStyles = getCompoundStyle({
            node,
            otherStyle: { width: cartWidth, height: cartHeight }
        });

        const asideStyles = {
            // ...(cartWidth ? { width: cartWidth } : {}),
            ...(mainBg ? { backgroundColor: mainBg } : {})
            // ...(cartHeight ? { height: cartHeight } : {})
        };

        const productStyleWithState = getStylesFromInteractiveStates({
            states,
            defaultStyle: expandedNode.typography?.product,
            stateManager,
            paths: ["expandedNode_typography_product"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const productSubtextStyleWithState = getStylesFromInteractiveStates({
            states,
            defaultStyle: expandedNode.typography?.productSubtext,
            stateManager,
            paths: ["expandedNode_typography_productSubtext"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const subtotalStyleWithState = getStylesFromInteractiveStates({
            states,
            defaultStyle: expandedNode.typography?.subtotals,
            stateManager,
            paths: ["expandedNode_typography_subtotals"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const totalStyleWithState = getStylesFromInteractiveStates({
            states,
            defaultStyle: expandedNode.typography?.total,
            stateManager,
            paths: ["expandedNode_typography_total"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const summarySubtextWithState = getStylesFromInteractiveStates({
            states,
            defaultStyle: expandedNode.typography?.summarySubtext,
            stateManager,
            paths: ["expandedNode_typography_summarySubtext"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const customerStyleWithState = getStylesFromInteractiveStates({
            states,
            defaultStyle: expandedNode.customerChip,
            stateManager,
            paths: ["expandedNode_customerChip"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const customerTypographyStyleWithState = getStylesFromInteractiveStates({
            states,
            defaultStyle: expandedNode.typography?.customer,
            stateManager,
            paths: ["expandedNode_typography_customer"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        //Icons
        let DeleteIcon = (
            <svg style={{ width: "32px", height: "32px" }} viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0.5" y="0.5" width="17" height="17" rx="3.5" stroke="#ABA7AF" />
                <path
                    d="M5 5L13 13M5 13L13 5"
                    stroke="#ABA7AF"
                    strokeWidth="1.08333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        );

        let CustomerIcon = (
            <svg
                style={{ width: "32px", height: "32px" }}
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <circle cx="12" cy="9" r="4" stroke="#758696" stroke-width="1.5" />
                <path
                    d="M6 19L6.0883 18.7351C6.52832 17.4151 6.74832 16.755 7.2721 16.3775C7.79587 16 8.49159 16 9.88304 16H14.117C15.5084 16 16.2041 16 16.7279 16.3775C17.2517 16.755 17.4717 17.4151 17.9117 18.7351L18 19"
                    stroke="#758696"
                    stroke-width="1.5"
                    stroke-linecap="round"
                />
            </svg>
        );

        if (expandedNode?.customerXIcon?.src) {
            const style = getStylesFromInteractiveStates({
                states,
                defaultStyle: expandedNode.customerXIcon?.style,
                stateManager,
                paths: ["expandedNode_customerXIcon_style"],
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });

            const colors = getColorsFromInteractiveStates({
                states,
                stateManager,
                paths: ["expandedNode_customerXIcon_colors"],
                defaultColors: expandedNode.customerXIcon?.colors,
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });

            DeleteIcon = (
                <DynamicIcon name={expandedNode.customerXIcon.src} style={{ width: "18px", ...style }} colors={colors} />
            );
        }

        if (expandedNode?.customerIcon?.src) {
            const style = getStylesFromInteractiveStates({
                states,
                defaultStyle: expandedNode.customerIcon?.style,
                stateManager,
                paths: ["expandedNode_customerIcon_style"],
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });

            const colors = getColorsFromInteractiveStates({
                states,
                stateManager,
                paths: ["expandedNode_customerIcon_colors"],
                defaultColors: expandedNode.customerIcon?.colors,
                ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
            });

            CustomerIcon = (
                <DynamicIcon name={expandedNode.customerIcon.src} style={{ width: "18px", ...style }} colors={colors} />
            );
        }

        const fallbackStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: fallback?.style || {},
            stateManager,
            paths: ["fallback_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const prependStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: prepend?.style || {},
            stateManager,
            paths: ["prepend_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const appendStyle = getStylesFromInteractiveStates({
            states,
            defaultStyle: append?.style || {},
            stateManager,
            paths: ["append_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        return (
            <div id={id} {...attr} className={builderSimpleCartClass} style={cartStyles}>
                <aside className="expanded-cart unfixed" ref={setElement} style={asideStyles}>
                    <section>
                        {cNode.expandedNode.showActiveCustomer !== false && customer && (
                            <SimpleCartCustomer
                                onClose={onRemoveCustomer}
                                onEdit={onEditCustomer}
                                dynamicData={dynamicData || ""}
                                customerIcon={CustomerIcon}
                                closeCustomerIcon={DeleteIcon}
                                customerTypography={customerTypographyStyleWithState}
                                customerChipStyle={customerStyleWithState}
                                prepend={{ style: prependStyle, value: prepend?.value || "" }}
                                append={{ style: appendStyle, value: append?.value || "" }}
                                fallback={{ style: fallbackStyle, value: fallback?.value || "" }}
                            />
                        )}
                        {!customFee && (!products || products.length === 0) && (!customSales || customSales.length === 0) ? (
                            children
                        ) : (
                            <>
                                {[...products].reverse().map((p, index) => {
                                    const taxDetails = calculatedTaxDetails?.products[p.internalId || p.id];
                                    // Line item shows only product (item) discount; cart discount is shown once in footer
                                    const calculatedAmounts =
                                        taxDetails != null
                                            ? {
                                                  discountAmount: (taxDetails.prodDiscount ?? 0) * p.quantity,
                                                  feeAmount: taxDetails.fee ?? 0,
                                                  discountLabel:
                                                      taxDetails.prodDiscountLabel ?? p.discount?.label,
                                                  feeLabel: taxDetails.feeLabel ?? p.fee?.label
                                              }
                                            : undefined;
                                    return (
                                        <SimpleCartProduct
                                            key={index}
                                            product={p}
                                            calculatedAmounts={calculatedAmounts}
                                            onRemove={() => {
                                                onProductRemove && onProductRemove(p.internalId);
                                            }}
                                            onSelect={() => {
                                                onProductSelection && onProductSelection(p);
                                            }}
                                            dividerBg={dividerColor}
                                            productStyle={productStyleWithState}
                                            productSubtextStyle={productSubtextStyleWithState}
                                            formatCurrency={formatC}
                                            stateManager={stateManager}
                                            disabled={disabled}
                                            isCFD={isCFD}
                                            expandedNode={expandedNode}
                                        />
                                    );
                                })}

                                {customSales?.map((sale, index) => (
                                    <SimpleCartCustomSales
                                        key={index}
                                        customSale={sale}
                                        onSelect={() => {
                                            onCustomSaleSelection && onCustomSaleSelection(sale);
                                        }}
                                        onRemove={() => {
                                            onCustomSaleRemove && onCustomSaleRemove(sale.id);
                                        }}
                                        dividerBg={dividerColor}
                                        productStyle={productStyleWithState}
                                        productSubtextStyle={productSubtextStyleWithState}
                                        formatCurrency={formatC}
                                        stateManager={stateManager}
                                        disabled={disabled}
                                        isCFD={isCFD}
                                        expandedNode={expandedNode}
                                    />
                                ))}

                                {expandedNode.showOrderNotes && orderNotes && (
                                    <div style={{ display: "flex", flexDirection: "column" }}>
                                        <span className="cart-order-notes-label">
                                            {expandedNode.showOrderNotesLabel && expandedNode.orderNotesLabel}
                                        </span>
                                        <span className="cart-order-notes">{orderNotes}</span>
                                    </div>
                                )}
                            </>
                        )}
                    </section>
                    <SimpleCartFooter
                        tax={tax}
                        total={total}
                        taxIncl={taxIncl}
                        bg={totalBG}
                        discount={discount}
                        subtotal={subtotal}
                        showTaxes={expandedNode.showTaxes}
                        dividerBg={dividerColor}
                        totalStyle={totalStyleWithState}
                        subtotalStyle={subtotalStyleWithState}
                        taxesLabel={expandedNode.customizeTaxes}
                        showDiscount={expandedNode.showCartDiscount}
                        showSubtotal={expandedNode.showCartSubtotal}
                        subtotalLabel={expandedNode.customizeCartSubtotal}
                        showTotal={expandedNode.showCartTotal}
                        totalLabel={expandedNode.customizeCartTotal}
                        balanceLabel={expandedNode.balanceDueLabel}
                        showBalance={expandedNode.showBalance}
                        formatCurrency={formatC}
                        onRemoveDiscount={onRemoveDiscount}
                        payments={payments}
                        cartFees={customFee}
                        calculatedCartFees={calculatedTaxDetails?.cartFees}
                        cartTotal={cartTotal}
                        orderTotalLabel={expandedNode.orderTotalLabel}
                        showOrderTotal={expandedNode.showOrderTotal}
                        summarySubtextStyle={summarySubtextWithState}
                        onCartFeeRemove={onCartFeeRemove}
                    />
                </aside>
            </div>
        );
    }
};

export default BuilderSimpleCartUnfixed;
